import React from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetUnreadPostsQuery,
  useGetUnreadEventsQuery,
  useGetUnreadTasksQuery,
  useMarkPostsAsReadMutation,
  useMarkEventsAsReadMutation,
  useMarkTasksAsReadMutation,
  useGetActivePollsQuery,
  useGetActiveVotesQuery,
} from "../../shared";
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";

const NotificationDropdown = ({ onClose }) => {
  const navigate = useNavigate();

  // Fetch unread posts, events, and tasks
  const { data: unreadPostsData, isLoading: postsLoading } =
    useGetUnreadPostsQuery();
  const { data: unreadEventsData, isLoading: eventsLoading } =
    useGetUnreadEventsQuery();
  const { data: unreadTasksData, isLoading: tasksLoading } =
    useGetUnreadTasksQuery();

  const { canAccess: votingEnabled } = useFeatureToggle("voting");
  const { data: activePollsData } = useGetActivePollsQuery(undefined, {
    skip: !votingEnabled,
  });
  const { data: activeVotesData } = useGetActiveVotesQuery(undefined, {
    skip: !votingEnabled,
  });

  // Mark as read mutations
  const [markPostsAsRead] = useMarkPostsAsReadMutation();
  const [markEventsAsRead] = useMarkEventsAsReadMutation();
  const [markTasksAsRead] = useMarkTasksAsReadMutation();

  const unreadPosts = unreadPostsData?.data?.posts || [];
  const unreadEvents = unreadEventsData?.data?.events || [];
  const unreadTasks = unreadTasksData?.data?.tasks || [];
  const activePolls = activePollsData?.data.polls || [];
  const activeVotes = activeVotesData?.data.votes || [];

  // Combine posts, events, and tasks into unified notification list
  const notifications = [
    ...unreadPosts.map((post) => ({
      id: `post-${post.id}`,
      type: "post",
      postId: post.id,
      title: post.title,
      message: `New post by ${post.author.firstName} ${post.author.lastName}`,
      time: formatTimeAgo(post.createdAt),
      createdAt: post.createdAt,
    })),
    ...unreadEvents.map((event) => ({
      id: `event-${event.id}`,
      type: "event",
      eventId: event.id,
      title: event.title,
      message: `Event on ${formatEventDate(event.startTime)}`,
      time: formatTimeAgo(event.createdAt),
      createdAt: event.createdAt,
    })),
    ...unreadTasks.map((task) => ({
      id: `task-${task.id}`,
      type: "task",
      taskId: task.id,
      title: task.title,
      message: task.assignedBy
        ? `Assigned by ${task.assignedBy.firstName} ${task.assignedBy.lastName}`
        : `Status changed to ${task.status}`,
      time: formatTimeAgo(task.updatedAt || task.createdAt),
      createdAt: task.updatedAt || task.createdAt,
      priority: task.priority,
    })),
    ...activePolls.map((poll) => ({
      id: `poll-${poll.id}`,
      type: "poll",
      pollId: poll.id,
      title: poll.title,
      message: poll.closeAt
        ? `Closes ${new Date(poll.closeAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
        : "Open for responses",
      time: formatTimeAgo(poll.createdAt),
      createdAt: poll.createdAt,
    })),
    ...activeVotes.map((vote) => ({
      id: `vote-${vote.id}`,
      type: "vote",
      voteId: vote.id,
      title: vote.title,
      message: vote.closeAt
        ? `Closes ${new Date(vote.closeAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
        : "Open for ballots",
      time: formatTimeAgo(vote.createdAt),
      createdAt: vote.createdAt,
    })),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Most recent first

  // Format time ago helper
  function formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  }

  // Format event date helper
  function formatEventDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.type === "post") {
      navigate(`/dashboard/feed`);
    } else if (notification.type === "event") {
      navigate(`/dashboard/events`);
    } else if (notification.type === "task") {
      navigate(`/dashboard/tasks`);
    } else if (notification.type === "poll" || notification.type === "vote") {
      navigate(`/dashboard/voting`);
    }
    onClose();
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await Promise.all([
        markPostsAsRead().unwrap(),
        markEventsAsRead().unwrap(),
        markTasksAsRead().unwrap(),
      ]);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  const isLoading = postsLoading || eventsLoading || tasksLoading;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Dropdown */}
      <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-elevated border border-neutral-200 z-50">
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <h3 className="font-semibold">Notifications</h3>
          {notifications.length > 0 && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              {notifications.length}
            </span>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center text-neutral-600">
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-neutral-600">
              <p>No new notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  className="w-full text-left p-4 hover:bg-neutral-50 transition-colors bg-primary-50"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-1">
                      {notification.type === "post" ? (
                        <svg
                          className="w-4 h-4 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                          />
                        </svg>
                      ) : notification.type === "event" ? (
                        <svg
                          className="w-4 h-4 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      ) : notification.type === "poll" ? (
                        <ChartBarIcon className="w-4 h-4 text-green-600" />
                      ) : notification.type === "vote" ? (
                        <ShieldCheckIcon className="w-4 h-4 text-primary-600" />
                      ) : (
                        <ClipboardDocumentListIcon className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {notification.title}
                      </p>
                      <p className="text-sm text-neutral-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-neutral-500 mt-2">
                        {notification.time}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-3 border-t border-neutral-200">
            <button
              onClick={handleMarkAllAsRead}
              className="w-full text-sm text-primary-700 font-medium hover:text-primary-800"
            >
              Mark all as read
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;
